import { ListItem, ListItemButton, Menu } from '@mui/material';
import React, { useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { SelectOption } from 'types/selectOption';

import * as Styled from './NavDrawer.styled';

function isConsentShellRoute(to: string) {
  return (
    to.startsWith('/request-identification') ||
    to.startsWith('/consent-insights') ||
    to.startsWith('/tag-versions') ||
    to.startsWith('/sitemap')
  );
}

function navigateTopWindow(pathname: string, search: string) {
  if (typeof window === 'undefined') {
    return;
  }

  const nextUrl = search ? `${pathname}?${search}` : pathname;
  const isIframe = window.self !== window.top;

  if (isIframe && isConsentShellRoute(pathname)) {
    window.top?.postMessage({ event: 'parent-navigate', data: nextUrl }, '*');
  }
}

export default function NavItem(
  props: {
    to?: string;
    icon: React.ReactNode;
    children: React.ReactNode;
    active?: boolean;
    isMini: boolean;
    subpages?: SelectOption[];
  } & React.ComponentProps<typeof ListItem>
) {
  const { to, icon, active, isMini, children, subpages, ...rest } = props;
  const [searchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const classNameObject: any = {};
  if (active === true) classNameObject.className = 'active';

  const linkSearch = searchParams.toString();

  const buttonProps = subpages
    ? {
        onClick: handleClick,
      }
    : {
        ...(to && isConsentShellRoute(to)
          ? {
              component: 'a',
              href: linkSearch ? `${to}?${linkSearch}` : to,
              onClick: (event: React.MouseEvent<HTMLElement>) => {
                navigateTopWindow(to, linkSearch);
                if (window.self !== window.top) {
                  event.preventDefault();
                }
              },
            }
          : {
              component: NavLink,
              to: {
                pathname: to,
                search: linkSearch,
              },
            }),
      };

  return (
    <>
      <ListItem disablePadding>
        <Styled.ListItemButton
          disableGutters={isMini}
          {...buttonProps}
          {...classNameObject}
          {...rest}
          sx={{
            ...rest.sx,
            paddingRight: 0,
          }}
        >
          <Styled.ListItemIcon
            style={{
              padding: isMini ? 0 : undefined,
              minWidth: isMini ? 'unset' : '32px',
              margin: isMini ? 'auto' : undefined,
            }}
          >
            {icon}
          </Styled.ListItemIcon>
          {!isMini && <Styled.ListItemText>{children}</Styled.ListItemText>}
        </Styled.ListItemButton>
      </ListItem>
      {subpages && (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {subpages.map((subpage) => (
            <ListItemButton
              key={subpage.value}
              disabled={subpage.disabled}
              {...(isConsentShellRoute(subpage.value)
                ? {
                    component: 'a',
                    href: linkSearch ? `${subpage.value}?${linkSearch}` : subpage.value,
                    onClick: (event: React.MouseEvent<HTMLElement>) => {
                      handleClose();
                      navigateTopWindow(subpage.value, linkSearch);
                      if (window.self !== window.top) {
                        event.preventDefault();
                      }
                    },
                  }
                : {
                    component: NavLink,
                    onClick: () => {
                      handleClose();
                    },
                    to: {
                      pathname: subpage.value,
                      search: linkSearch,
                    },
                  })}
            >
              {subpage.icon && <Styled.ListItemIcon style={{ minWidth: '32px' }}>{subpage.icon}</Styled.ListItemIcon>}
              {subpage.label}
            </ListItemButton>
          ))}
        </Menu>
      )}
    </>
  );
}
